
import Vue from 'vue';
import { namespace } from 'vuex-class';
import { Component, Emit, InjectReactive, Prop } from 'vue-property-decorator';

import BasicButton from '@/ui-components/BasicButton.vue';
import CompetitionStageHeader from '@/components/sports/common/competition/fixtures/CompetitionStageHeader.vue';
import EventCard from '@/components/sports/common/event/EventCard.vue';
import FilterBarProviderSeo from '@/components/filter-bar/seo/FilterBarProviderSeo.vue';
import ResetButton from '@/components/buttons/ResetButton.vue';
import Subheading from '@/components/sports/common/Subheading.vue';
import AdUnit from '@/components/AdUnit.vue';

import { ObjectType, SportCompetitionV2 } from '@/@types/graphql-types';

import { SnowplowSportContext } from '@/helpers/tracking/providers';
import { TrackingHelper } from '@/helpers/tracking/tracking-helper';

import { SingleStepContext } from '@/helpers/sports/config';

import type { FilterCollection } from '@/stores/filter.store';
import type { SportType } from '@/@types/graphql-types';
import type { UserSettings } from '@/stores/user.store';
import type { WebLocale } from '@/enums/web-locale';
import type Provider from '@/interfaces/provider';
import { CompetitionStages } from '@/interfaces/sport/competition-stages';
import { GetSportProvidersByShortNamesQuery } from '@/pages/graphql/queries/GetSportProvidersByShortNames.query';

const Constant = namespace('constant');
const Filter = namespace('filter');
const Language = namespace('language');
const User = namespace('user');

@Component({
	name: 'CompetitionFixtures',
	components: {
		BasicButton,
		FilterBarProviderSeo,
		Subheading,
		CompetitionStageHeader,
		EventCard,
		ResetButton,
		AdUnit,
	},
})
export default class CompetitionFixtures extends Vue {
	@Prop({ required: true }) isDataLoading: boolean;
	@Prop({ required: true }) fixtures: SportCompetitionV2;
	@Prop({ required: true }) context: SingleStepContext;
	@Prop({ required: true, default: () => [] }) competitionStages: CompetitionStages[];

	@Constant.Getter providersByShortName: Record<string, Provider>;

	@Language.State webLocale: WebLocale;
	@Language.Getter language: string;

	@Filter.Getter currentFilters: FilterCollection;

	@User.State settings: UserSettings;

	@InjectReactive() sportProvidersByShortName: GetSportProvidersByShortNamesQuery['packages'];

	get providers() {
		return (
			this.sportProvidersByShortName
				.map(({ shortName }) => this.providersByShortName[shortName])
				// if BE and FE are out of sync some shortnames might not get matched and be undefined
				.filter(provider => provider != null)
		);
	}

	get hasNextPage() {
		return this.fixtures?.sportEventsV2?.pageInfo?.hasNextPage ?? false;
	}

	get sportContext() {
		const sportType = this.$route.meta?.sport as SportType;
		const sportContext = new SnowplowSportContext(sportType, ObjectType.SportCompetition);
		sportContext.competitionId = this.fixtures.objectId;
		return sportContext;
	}

	@Emit('load-next')
	onLoadNext() {
		TrackingHelper.trackEvent(
			'userinteraction',
			{
				action: 'more_providers_click',
				label: 'fixtures',
			},
			[this.sportContext]
		);
	}

	toggleProvider(data: { filterName: keyof FilterCollection; value: any; selectedState: 1 | 0 }) {
		TrackingHelper.trackEvent(
			'userinteraction',
			{
				action: 'filter_toggled',
				label: data.filterName,
				property: data.value ?? 'all',
				value: data.selectedState,
			},
			[this.sportContext]
		);
	}
}
